// OP-3605 — Catálogo (ver ESL1; criar e trocar regime ESL17). O catálogo é
// global: não tem escopo de praça, porque item é definição, não posição.
//
// Trocar o regime de um item (peça ↔ quantidade) é a operação perigosa da
// tela: o servidor já devolve o veredito pronto em `trocaControle`, e é ele
// que manda — a tela não refaz a conta.
import { FC, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useEstoqueCatalogo, useEstoqueCategorias } from '../../../../hooks/estoque'
import { usePerfilEstoque } from '../../../../hooks/estoque/perfil'
import { createEstoqueCategoria, createEstoqueItem, mudarControleEstoqueItem } from '../../../../Service/estoque'
import { CatalogoFilters, Controle, ItemCatalogoRow, TIPO_DEMANDA_LABEL, TipoDemanda } from '../../../../types/estoque'
import {
  FormItem, ITEM_VAZIO, TIPOS_DEMANDA_ITEM, impedimentoNovaCategoria, impedimentoNovoItem, montarNovoItem, rotuloCategoria,
} from './regras'
import {
  BD, Button, CaixaLista, Card, Field, FiltersRow, Folha, FolhaOverlay, FolhaPega, FolhaTopo, Input, ListaCartoes, PageSubtitle, PageTitle, Select, SoCelular, SoDesktop, Spinner, StatusBadge, TabelaWrap, Table,
} from '../../styles'
import { tratarErro } from '../erros'
import { plural } from '../formato'
import { LOCAL_EXECUTION } from '../../../../utils/featureFlag'
import { MoneyInputOpcional } from '../../MoneyInput'

const COLS = 8

const CONTROLE_LABEL: Record<Controle, string> = { SERIAL: 'Peça (série)', QUANTIDADE: 'Quantidade' }
/** O regime oposto — é para ele que `trocaControle` já respondeu. */
const oposto = (c: Controle): Controle => (c === 'SERIAL' ? 'QUANTIDADE' : 'SERIAL')
const rotuloTiposDemanda = (csv: string | null): string => {
  const tipos = String(csv ?? '').split(',').map((t) => t.trim()).filter(Boolean)
  return tipos.length ? tipos.map((t) => TIPO_DEMANDA_LABEL[t as TipoDemanda] ?? t).join(' · ') : '—'
}

export const EstoqueCatalogo: FC = () => {
  const navigate = useNavigate()
  const { pode } = usePerfilEstoque()
  const podeCatalogo = pode('CATALOGO')
  const [draft, setDraft] = useState<CatalogoFilters>({ page: 1 })
  const [applied, setApplied] = useState<CatalogoFilters>({ page: 1 })
  const { rows, total, lastPage, isLoading, isError, isUnauthorized, mutate } = useEstoqueCatalogo(applied)
  const [trocando, setTrocando] = useState<ItemCatalogoRow | null>(null)
  const [busy, setBusy] = useState(false)
  const pagina = Math.max(1, Number(applied.page) || 1)
  const categoriasQ = useEstoqueCategorias()
  const [criando, setCriando] = useState(false)
  const [form, setForm] = useState<FormItem>(ITEM_VAZIO)
  const [novaCategoria, setNovaCategoria] = useState('')
  const [criandoCategoria, setCriandoCategoria] = useState(false)

  if (isUnauthorized && !LOCAL_EXECUTION) return <Navigate to="/auth" replace />

  const trocar = async () => {
    if (!trocando) return
    setBusy(true)
    try {
      await mudarControleEstoqueItem({ id: trocando.id, controle: oposto(trocando.controle) })
      toast.success('Regime do item alterado')
      setTrocando(null)
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  // Só a página carregada e filtrada: o gate é um aviso barato, não a
  // garantia. Quem recusa é a UNIQUE do banco, inclusive contra item inativo,
  // que esta lista nem traz por padrão.
  const impItem = impedimentoNovoItem(form, rows.map((r) => r.codigo ?? ''))
  const impCategoria = impedimentoNovaCategoria(novaCategoria, categoriasQ.data)

  const criarCategoria = async () => {
    setBusy(true)
    try {
      const { data } = await createEstoqueCategoria({ nome: novaCategoria.trim() })
      toast.success('Categoria criada')
      setNovaCategoria(''); setCriandoCategoria(false)
      const id = data?.data?.id
      if (id) setForm((f) => ({ ...f, categoriaId: Number(id) }))
      // Fora do try da criação: a categoria JÁ existe. Uma falha aqui é da
      // releitura, e tratá-la no mesmo catch mostrava erro depois do sucesso
      // e, num 401, jogava fora o formulário meio preenchido.
      try { await categoriasQ.mutate() } catch { /* a lista mostra o próprio erro */ }
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
    } finally {
      setBusy(false)
    }
  }

  const criarItem = async () => {
    setBusy(true)
    try {
      await createEstoqueItem(montarNovoItem(form))
      toast.success('Item criado')
      setForm(ITEM_VAZIO); setCriando(false)
      await mutate()
    } catch (err) {
      const e = tratarErro(err)
      if (e.tipo === 'sessao' && !LOCAL_EXECUTION) { navigate('/auth'); return }
      toast.error(e.texto)
      if (e.tipo === 'estado') await mutate()
    } finally {
      setBusy(false)
    }
  }

  /** O mesmo formulário nas duas larguras: no desktop abre no lugar; no celular, em folha. */
  const formNovoItem = () => (
    <>
            <>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Novo item</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                <Field>
                  <label>Categoria</label>
                  {categoriasQ.isError ? (
                    <div style={{ fontSize: 13, color: BD.red }}>
                      Não foi possível carregar as categorias.{' '}
                      <Button $variant="secondary" onClick={() => categoriasQ.mutate()}>Tentar de novo</Button>
                    </div>
                  ) : (
                    <Select value={form.categoriaId ?? ''} onChange={(e) => setForm({ ...form, categoriaId: Number(e.target.value) || null })}>
                      <option value="">{categoriasQ.isLoading ? 'carregando…' : categoriasQ.data.length ? '— escolha —' : 'nenhuma categoria ainda'}</option>
                      {categoriasQ.data.map((c) => <option key={c.id} value={c.id}>{rotuloCategoria(c)}</option>)}
                    </Select>
                  )}
                  {!criandoCategoria ? (
                    <div style={{ marginTop: 6 }}>
                      <Button $variant="secondary" onClick={() => setCriandoCategoria(true)}>Criar uma categoria</Button>
                    </div>
                  ) : (
                    <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                      <Input value={novaCategoria} maxLength={80} placeholder="Nome da nova categoria" aria-label="Nome da nova categoria" onChange={(e) => setNovaCategoria(e.target.value)} />
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Button $variant="secondary" disabled={busy || !!impCategoria} onClick={criarCategoria}>Criar</Button>
                        <Button $variant="secondary" disabled={busy} onClick={() => { setCriandoCategoria(false); setNovaCategoria('') }}>Descartar</Button>
                      </div>
                      {impCategoria && novaCategoria && <span style={{ fontSize: 12, color: BD.grayText }}>{impCategoria}</span>}
                      <span style={{ fontSize: 12, color: BD.grayText }}>Confira a lista antes: a mesma categoria com outro nome separa itens que deveriam ficar juntos.</span>
                    </div>
                  )}
                </Field>
                <Field><label>Código</label><Input value={form.codigo} maxLength={40} onChange={(e) => setForm({ ...form, codigo: e.target.value })} /></Field>
                <Field><label>Nome</label><Input value={form.nome} maxLength={120} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></Field>
                <Field>
                  <label>Como é controlado</label>
                  <Select value={form.controle} onChange={(e) => setForm({ ...form, controle: e.target.value as Controle | '' })}>
                    <option value="">— escolha —</option>
                    <option value="SERIAL">{CONTROLE_LABEL.SERIAL}</option>
                    <option value="QUANTIDADE">{CONTROLE_LABEL.QUANTIDADE}</option>
                  </Select>
                  <div style={{ fontSize: 12, color: BD.grayText, marginTop: 4 }}>
                    Peça tem série e patrimônio próprios; quantidade é saldo. Isso só muda enquanto o item não tiver peça, saldo nem movimento.
                  </div>
                </Field>
                <Field><label>Unidade de medida</label><Input value={form.unidadeMedida} maxLength={20} onChange={(e) => setForm({ ...form, unidadeMedida: e.target.value })} /></Field>
                <Field><label>Unidades por caixa (opcional)</label><Input type="number" min={1} value={form.fatorCaixa} onChange={(e) => setForm({ ...form, fatorCaixa: e.target.value })} /></Field>
                <Field><label>Estoque mínimo (opcional)</label><Input type="number" min={0} value={form.estoqueMinimo} onChange={(e) => setForm({ ...form, estoqueMinimo: e.target.value })} /></Field>
                <Field><label>Valor de reposição (opcional)</label><MoneyInputOpcional value={form.valorReposicao} onChange={(v) => setForm({ ...form, valorReposicao: v })} /></Field>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Aparece em quais tipos de demanda</div>
                <div style={{ fontSize: 12, color: BD.grayText, marginBottom: 6 }}>Quem solicita só vê o item nos tipos marcados.</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {TIPOS_DEMANDA_ITEM.map((t) => {
                    const on = form.tiposDemanda.indexOf(t) >= 0
                    return (
                      <Button key={t} $variant={on ? 'primary' : 'secondary'}
                              onClick={() => setForm({ ...form, tiposDemanda: on ? form.tiposDemanda.filter((x) => x !== t) : [...form.tiposDemanda, t] })}>
                        {on ? '✓ ' : ''}{TIPO_DEMANDA_LABEL[t as TipoDemanda]}
                      </Button>
                    )
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button disabled={busy || !!impItem} onClick={criarItem}>Criar item</Button>
                <Button $variant="secondary" disabled={busy} onClick={() => { setCriando(false); setForm(ITEM_VAZIO) }}>Descartar</Button>
                {impItem && <span style={{ fontSize: 13, color: BD.grayText }}>{impItem}</span>}
              </div>
            </>
    </>
  )

  const cartao = (i: ItemCatalogoRow) => {
    const veredito = i.trocaControle
    const ehPeca = i.controle === 'SERIAL'
    return (
      <Card key={i.id} style={{ padding: 14, opacity: Number(i.ativo) ? 1 : 0.55 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ flexGrow: 1, fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: BD.grayText }}>{i.codigo ?? 'sem código'}</span>
          <StatusBadge $bg="#E0E7FF" $color="#3730A3">{CONTROLE_LABEL[i.controle]}</StatusBadge>
        </div>
        <div style={{ marginTop: 6, fontSize: 16, fontWeight: 600 }}>{i.nome}</div>
        <div style={{ marginTop: 2, fontSize: 13, color: BD.grayText }}>
          {i.categoria}
          {i.unidadeMedida ? ` · ${i.unidadeMedida}` : ''}
          {i.fatorCaixa ? ` · caixa com ${i.fatorCaixa}` : ''}
          {Number(i.ativo) ? '' : ' · inativo'}
        </div>
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BD.grayBorder}`, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
          <span style={{ flexGrow: 1 }}>
            <b>{ehPeca ? Number(i.unidadesCadastradas) : Number(i.saldoTotal)}</b>
            {ehPeca ? ' peças cadastradas' : ' no saldo'}
            {i.estoqueMinimo != null ? ` · mínimo ${i.estoqueMinimo}` : ''}
          </span>
          <span style={{ fontSize: 12, color: BD.grayText }}>{rotuloTiposDemanda(i.tiposDemanda)}</span>
        </div>
        {podeCatalogo && (veredito && veredito.ok ? (
          <Button style={{ marginTop: 12, width: '100%' }} $variant="secondary" disabled={busy} onClick={() => setTrocando(i)}>
            Trocar para {CONTROLE_LABEL[oposto(i.controle)].toLowerCase()}
          </Button>
        ) : (
          <div style={{ marginTop: 12, fontSize: 12, color: BD.grayText }}>
            {MOTIVO_TROCA[veredito?.erro ?? ''] ?? 'Troca de regime indisponível'}
          </div>
        ))}
      </Card>
    )
  }

  const linha = (i: ItemCatalogoRow) => {
    const veredito = i.trocaControle
    return (
      <tr key={i.id} style={{ opacity: Number(i.ativo) ? 1 : 0.55 }}>
        <td>
          <b>{i.nome}</b>
          <div style={{ fontSize: 12, color: BD.grayText }}>{i.codigo ?? 'sem código'}{Number(i.ativo) ? '' : ' · inativo'}</div>
        </td>
        <td style={{ fontSize: 13 }}>{i.categoria}</td>
        <td><StatusBadge $bg="#E0E7FF" $color="#3730A3">{CONTROLE_LABEL[i.controle]}</StatusBadge></td>
        <td style={{ fontSize: 13 }}>{i.unidadeMedida ?? '—'}{i.fatorCaixa ? ` · caixa com ${i.fatorCaixa}` : ''}</td>
        <td style={{ textAlign: 'right' }}>{i.controle === 'SERIAL' ? Number(i.unidadesCadastradas) : Number(i.saldoTotal)}</td>
        <td style={{ textAlign: 'right' }}>{i.estoqueMinimo ?? '—'}</td>
        <td style={{ fontSize: 12, color: BD.grayText }}>{rotuloTiposDemanda(i.tiposDemanda)}</td>
        <td>
          {podeCatalogo && (
            veredito && veredito.ok
              ? <Button $variant="secondary" disabled={busy} onClick={() => setTrocando(i)}>Trocar para {CONTROLE_LABEL[oposto(i.controle)].toLowerCase()}</Button>
              : <span style={{ fontSize: 12, color: BD.grayText }} title={veredito?.erro ?? ''}>{MOTIVO_TROCA[veredito?.erro ?? ''] ?? 'Troca de regime indisponível'}</span>
          )}
        </td>
      </tr>
    )
  }

  return (
    <>
      <PageTitle>Catálogo</PageTitle>
      <PageSubtitle>
        O que existe para ser pedido, comprado e contado. O catálogo é global — vale para todas as praças. O regime de um item (peça com série ou quantidade) decide como ele é controlado e só muda enquanto o item não tem história.
      </PageSubtitle>

      {podeCatalogo && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => setCriando(true)}>Novo item</Button>
            <span style={{ fontSize: 12, color: BD.grayText }}>
              Sem item no catálogo nada entra no estoque: a Entrada escolhe o que cadastrar daqui.
            </span>
          </div>
          {criando && <SoDesktop><div style={{ marginTop: 16 }}>{formNovoItem()}</div></SoDesktop>}
        </Card>
      )}

      {criando && (
        <SoCelular>
          <FolhaOverlay $open onClick={() => setCriando(false)} />
          <Folha $open role="dialog" aria-label="Novo item do catálogo">
            <FolhaPega />
            <FolhaTopo>
              <h2>Novo item</h2>
            </FolhaTopo>
            {formNovoItem()}
          </Folha>
        </SoCelular>
      )}

      <FiltersRow>
        <Field>
          <label>Nome, código ou categoria</label>
          <Input value={draft.busca ?? ''} onChange={(e) => setDraft((d) => ({ ...d, busca: e.target.value }))}
                 onKeyDown={(e) => e.key === 'Enter' && setApplied({ ...draft, page: 1 })} />
        </Field>
        <Field>
          <label>Categoria</label>
          <Select value={draft.categoriaId ?? ''} onChange={(e) => setDraft((d) => ({ ...d, categoriaId: e.target.value }))}>
            <option value="">{categoriasQ.isError ? 'todas (lista indisponível)' : 'todas'}</option>
            {categoriasQ.data.map((c) => <option key={c.id} value={c.id}>{rotuloCategoria(c)}</option>)}
          </Select>
        </Field>
        <Field>
          <label>Regime</label>
          <Select value={draft.controle ?? ''} onChange={(e) => setDraft((d) => ({ ...d, controle: e.target.value as Controle | '' }))}>
            <option value="">todos</option>
            <option value="SERIAL">{CONTROLE_LABEL.SERIAL}</option>
            <option value="QUANTIDADE">{CONTROLE_LABEL.QUANTIDADE}</option>
          </Select>
        </Field>
        <Field>
          <label>Inativos</label>
          <Select value={draft.inativos ?? ''} onChange={(e) => setDraft((d) => ({ ...d, inativos: e.target.value as 'true' | '' }))}>
            <option value="">só ativos</option>
            <option value="true">mostrar também os inativos</option>
          </Select>
        </Field>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}><Button $variant="secondary" onClick={() => setApplied({ ...draft, page: 1 })}>Aplicar</Button></div>
      </FiltersRow>

      {trocando && (
        <Card style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Trocar o regime de {trocando.nome}</div>
          <div style={{ fontSize: 13, color: BD.grayText, marginBottom: 12 }}>
            De <b>{CONTROLE_LABEL[trocando.controle]}</b> para <b>{CONTROLE_LABEL[oposto(trocando.controle)]}</b>. Isso muda como o item é contado daqui em diante. Só é possível porque ele ainda não tem peça cadastrada, saldo em estoque nem movimento no razão.
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button disabled={busy} onClick={trocar}>Confirmar troca</Button>
            <Button $variant="secondary" disabled={busy} onClick={() => setTrocando(null)}>Voltar</Button>
          </div>
        </Card>
      )}

      <CaixaLista>
        <div style={{ padding: '8px 12px', fontSize: 13, color: BD.grayText }}>{isLoading ? 'Carregando…' : plural(total, 'item', 'itens')}</div>
        <TabelaWrap>
          <Table>
            <thead><tr><th>Item</th><th>Categoria</th><th>Regime</th><th>Medida</th><th style={{ textAlign: 'right' }}>Peças / saldo</th><th style={{ textAlign: 'right' }}>Mínimo</th><th>Tipos de demanda</th><th /></tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24 }}><Spinner /></td></tr>}
              {!isLoading && isError && (
                <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.red }}>
                  Não foi possível carregar o catálogo. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
                </td></tr>
              )}
              {!isLoading && !isError && rows.length === 0 && <tr><td colSpan={COLS} style={{ textAlign: 'center', padding: 24, color: BD.grayText }}>Nenhum item com os filtros atuais.</td></tr>}
              {!isLoading && !isError && rows.map(linha)}
            </tbody>
          </Table>
        </TabelaWrap>

        <ListaCartoes>
          {isLoading && <Card style={{ textAlign: 'center', padding: 24 }}><Spinner /></Card>}
          {!isLoading && isError && (
            <Card style={{ padding: 20, color: BD.red }}>
              Não foi possível carregar o catálogo. <Button $variant="secondary" onClick={() => mutate()}>Tentar de novo</Button>
            </Card>
          )}
          {!isLoading && !isError && rows.length === 0 && <Card style={{ padding: 20, color: BD.grayText }}>Nenhum item com os filtros atuais.</Card>}
          {!isLoading && !isError && rows.map(cartao)}
        </ListaCartoes>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, padding: 12 }}>
          <Button $variant="secondary" disabled={pagina <= 1} onClick={() => setApplied((a) => ({ ...a, page: pagina - 1 }))}>‹ Anterior</Button>
          <span style={{ fontSize: 13, color: BD.grayText }}>Página {pagina}</span>
          <Button $variant="secondary" disabled={!!isError || isLoading || !!lastPage} onClick={() => setApplied((a) => ({ ...a, page: pagina + 1 }))}>Próxima ›</Button>
        </div>
      </CaixaLista>
    </>
  )
}

/**
 * Por que a troca de regime está indisponível, em português.
 *
 * Os códigos vêm prontos do servidor em `trocaControle.erro` (veredito de
 * `podeMudarControle` já calculado para o regime oposto): a tela traduz, não
 * recalcula.
 */
const MOTIVO_TROCA: Record<string, string> = {
  ITEM_COM_PATRIMONIO_CADASTRADO: 'Tem peça cadastrada: o regime não muda.',
  ITEM_COM_SALDO_EM_ESTOQUE: 'Tem saldo em estoque: o regime não muda.',
  ITEM_COM_HISTORICO: 'Já tem movimento no razão: o histórico passaria a ser lido na unidade errada.',
  CONTROLE_SEM_MUDANCA: 'O item já está neste regime.',
}

export default EstoqueCatalogo
